import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper, useUnsavedChanges } from "aic";
import { CommentReadDto } from "functions/api/comments/interface";
import { eAccessRights, eReleaseStatus, SearchableEditorBlocks } from "functions/api/enums";
import { TitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import { isTlfItemReadDto, TlfItemReadDto } from "functions/api/tlfItem/TlfItemReadDto";
import { patchTlfItem, PatchTlfItemDetails } from "functions/api/tlfItem/patchTlfItem";
import { toIndexDto } from "functions/toIndexDto";
import { isEqual, pick } from "lodash";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { createPatch, Patch } from "rfc6902";
import { eHierarchyLevel } from "../functions/api/enums";
import { IRequestStatus } from "../functions/api/interface";
import { saveTlfItem, SaveTlfItemDetails } from "../functions/api/tlfItem/saveTlfItem";
import { AppSettingsContext } from "./AppSettingsContext";
import { HierarchyContext } from "./HierarchyContext";
import { SignalRContext } from "./SignalRProvider";
import { TlfContext } from "./TlfContext";
import { TocContext } from "./TocContext";
import { SAVE_HIERARCHY, UPDATE_TLF_ITEM_INDEX } from "./hierarchy/interface";
import { HubGroupMessage, TlfUpdateActionSignal } from "./signalr/interface";
import { getTlfUrl } from "./tlf/getTlfUrl";
import {
  LOAD_TLF,
  PATCH_TLF_COMMENTS,
  PROCESS_SIGNALR_NOTIFICATION,
  REMOVE_TLF_COMMENT,
  SET_EDITOR_BLOCKS_SEARCH_PANEL,
  SET_VIEW_MODE,
  TlfUpdateMessage,
  UPDATE_TLF,
  UPDATE_TLF_COMMENT,
} from "./tlf/interface";
import { tlfContextInitialState } from "./tlf/tlfContextInitialState";
import { tlfContextReducer } from "./tlf/tlfContextReducer";
import { loadTlfViewModeFromStorage } from "./tlf/tlfViewModes";
import { processTlfActionFromSignal } from "./toc/processTlfActionFromSignal";
import { SAVING_TOC_ITEM_VIA_TLF } from "./toc/tocContextReducer";

export interface TlfVersion {
  id: string;
  azsolId: string;
  version: number;
}

export type TlfContextModalWindow =
  | "LoadJson"
  | "Load"
  | "Replacements"
  | "RemoveStyles"
  | "SaveAs"
  | "";

type TlfItem = TitlesFootnotesReadDto | TlfItemReadDto;
export interface TlfContextProviderProps<T extends TlfItem> {
  children: ReactNode;
  tlfData: T | null;
  setTlfData?: (ret: T | null) => void;
  isSubscribed: boolean;
  readOnly?: boolean;
}

/**
 * Provides TLF (Table, Listing, or Figure) context to child components.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 * @param params.tlfData - The TLF data to manage within the context
 * @param params.setTlfData - Optional. Function to update the TLF data
 * @param params.isSubscribed - Whether the component is subscribed to SignalR updates
 * @param params.readOnly - Optional. When true, the TLF is in read-only mode
 */
export const TlfContextProvider = <T extends TlfItem>({
  children,
  tlfData,
  setTlfData,
  isSubscribed,
  readOnly = false,
}: TlfContextProviderProps<T>): ReactNode => {
  // Router to move when saving
  const [searchParams] = useSearchParams();

  // Location data
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const hierarchy = hierarchyContext?.hierarchy;
  const hierarchyNodes = hierarchyContext?.hierarchyNodes;
  const hierarchyDispatch = hierarchyContext?.dispatch;
  const { state: tocState, dispatch: tocDispatch } = useContext(TocContext);
  const tlfItemIndex = hierarchyContext?.tlfItemIndex;
  const signalR = useContext(SignalRContext);
  const { navigate, registerUnsavedChanges, navigateAfterSave } = useUnsavedChanges("tlf-context");
  const [state, dispatch] = useReducer(tlfContextReducer, tlfContextInitialState);

  // Load view mode from localStorage on initialization
  useEffect(() => {
    const savedViewMode = loadTlfViewModeFromStorage();
    if (savedViewMode.value !== state.tlfViewMode?.value) {
      dispatch({
        operation: SET_VIEW_MODE,
        calledFrom: "TlfContextProvider:InitializeViewMode",
        tlfViewMode: savedViewMode,
      });
    }
  }, [state.tlfViewMode?.value]);

  // Make sure TLF data and state are aligned
  // If not marked as changed or for direct push, then load the new data into the state
  // If marked as a change or for direct push, then push the change up
  // Changed & saved - setTlfData should already have been called by the save method
  useEffect(() => {
    /* istanbul ignore else */
    if (tlfData) {
      if (!isEqual(tlfData, state.tlfData)) {
        if (
          tlfData.id !== state.tlfData?.id ||
          (!state.changed &&
            !state.forcePushToParent &&
            state.ixSaveVersion === state.ixTlfDataVersion)
        ) {
          dispatch({
            operation: LOAD_TLF,
            calledFrom: "TlfContextProvider:Initial",
            tlfData,
            isSubscribed,
          });
        } else if (setTlfData && state.tlfData) {
          setTlfData(state.tlfData as T);
        }
      }
    }
  }, [
    setTlfData,
    state.changed,
    state.tlfData,
    tlfData,
    state.forcePushToParent,
    isSubscribed,
    state.ixSaveVersion,
    state.ixTlfDataVersion,
  ]);

  // SignalR Group
  const signalRGroup = useMemo(() => (state.tlfData ? `tlf${state.tlfData?.id}` : null), [state]);

  // Join SignalR Group
  const [lastSignalRGroupRequest, setLastSignalRGroupRequest] = useState<string>("❄️");
  const sendGroupRequest = useEffectEvent(async (signalRGroup: string) => {
    signalR!.connectToGroup(signalRGroup);
    setLastSignalRGroupRequest(signalRGroup);
  });
  useEffect(() => {
    if (
      signalR?.connection?.state === HubConnectionState.Connected &&
      signalRGroup &&
      lastSignalRGroupRequest !== signalRGroup
    ) {
      sendGroupRequest(signalRGroup);
    }
  }, [lastSignalRGroupRequest, signalR?.connection?.state, signalRGroup]);

  // Get Search Editor Texts State from URL
  useEffect(() => {
    if (
      hierarchy &&
      state.tlfData &&
      searchParams.has("searchText") &&
      searchParams.has("blocksToSearch")
    ) {
      const searchText = searchParams.get("searchText")!;
      const blockValuesToSearch = searchParams.getAll("blocksToSearch");
      const blocksToSearch = blockValuesToSearch
        .filter(
          (label) =>
            Object.values(SearchableEditorBlocks).findIndex((value) => value === label) > -1,
        )
        .map(
          (label) =>
            Object.keys(SearchableEditorBlocks)[
              Object.values(SearchableEditorBlocks).findIndex((value) => value === label)
            ] as SearchableEditorBlocks,
        );
      const urlEditorBlocksSearch = {
        searchText: searchText,
        blocksToSearch: blocksToSearch,
        showSearchPanel: true,
      };

      if (!isEqual(urlEditorBlocksSearch, state.editorBlocksSearchPanelStatus)) {
        dispatch({
          operation: SET_EDITOR_BLOCKS_SEARCH_PANEL,
          calledFrom: "TlfContextProvider:UrlSearchEditorText",
          editorBlocksSearchPanelStatus: {
            searchText,
            blocksToSearch,
            showSearchPanel: true,
          },
        });
        // Navigate to TLF URL without search params
        navigate(getTlfUrl(hierarchy, state.tlfData), { replace: true });
      }
    }
  }, [hierarchy, navigate, searchParams, state.editorBlocksSearchPanelStatus, state.tlfData]);

  const [receivedMessages, setReceivedMessages] = useState<TlfUpdateMessage[]>([]);

  /** Save/Save as */
  const [saveRequestStatus, setSaveRequestStatus] = useState<IRequestStatus<SaveTlfItemDetails>>({
    requesting: false,
    error: false,
  });

  const sendSignalRPatch = useCallback(
    async (newTlfData: T) => {
      /* istanbul ignore else */
      if (signalRGroup && signalR) {
        const lastSave = state.tlfDataVersions[state.ixSaveVersion];
        /* istanbul ignore else */
        if (lastSave) {
          // Only compare the keys that are in T
          const allowedKeys = Object.keys(lastSave).filter(
            (k) => !["saved", "changed", "inError", "history"].includes(k),
          );
          const compareResult = createPatch(
            pick(lastSave, allowedKeys),
            pick(newTlfData, allowedKeys),
          );
          const signalRUpdate: TlfUpdateActionSignal = {
            updatedItemId: lastSave.id,
            patch: compareResult,
          };
          const sentMessage = await signalR.sendTlfUpdateToGroup(signalRUpdate, signalRGroup);
          const newTlfMessage: TlfUpdateMessage = {
            messageId: sentMessage.messageId,
            updatedId: sentMessage.groupName,
            updateTS: new Date(sentMessage.timeStamp),
          };
          const newReceivedMessages = [...receivedMessages, newTlfMessage];
          setReceivedMessages(newReceivedMessages);
        }
      }
    },
    [receivedMessages, signalR, signalRGroup, state.ixSaveVersion, state.tlfDataVersions],
  );

  const saveTlf = useCallback(
    async (
      /* new AZSOL ID */
      azsolId?: string,
      /* new Version number */
      version?: number,
      /* new Domain */
      domain?: string,
      /* Hierarchy to save to */
      primaryHierarchyId?: string,
      /* new Additional information */
      additionalInformation?: string,
      /* Original object level */
      copiedFromHierarchyLevel?: eHierarchyLevel,
      /* New Toc Number */
      tocNumber?: string,
      /* New SAP Reference */
      sapReference?: string,
    ) => {
      /* istanbul ignore else */
      if (hierarchy && state.tlfData && isTlfItemReadDto(state.tlfData)) {
        const tlfData = state.tlfData as TlfItemReadDto;
        const response = await loadWrapper(
          saveTlfItem,
          {
            tlfItem: {
              ...tlfData,
              tocNumber: tocNumber ?? state.tlfData.tocNumber,
              sapReference: sapReference ?? tlfData.sapReference,
            },
            newDetails:
              azsolId && version && additionalInformation !== undefined && primaryHierarchyId
                ? {
                    azsolId,
                    version,
                    domain: domain ?? "",
                    additionalInformation,
                    primaryHierarchyId,
                  }
                : undefined,
            copiedFromHierarchyLevel: copiedFromHierarchyLevel,
          },
          saveRequestStatus,
          setSaveRequestStatus,
          appSettings.backEndUrl,
        );
        /* istanbul ignore else */
        if (response?.success === false) {
          console.warn(
            `Save failed id: ${azsolId ?? tlfData.azsolId}/${version ?? tlfData.version} failed`,
          );
          console.warn(response.errorText);
          return;
        } else if (response?.success) {
          // Update hierarchy level things first
          const newTlfItemIndex = toIndexDto(response.data);
          const newTlfIndex = [
            ...(
              tlfItemIndex ??
              // istanbul ignore next
              []
            ).filter(
              (tlf) =>
                tlf.azsolId !== response.data.azsolId && tlf.version !== response.data.version,
            ),
            newTlfItemIndex,
          ].sort((a, b) => a.azsolId.localeCompare(b.azsolId));
          hierarchyDispatch?.({
            operation: UPDATE_TLF_ITEM_INDEX,
            tlfItemIndex: newTlfIndex,
          });
          // Update any ARS changes that have been made by the TLF editor
          hierarchyDispatch?.({ operation: SAVE_HIERARCHY });
          // Update to holding object if possible, otherwise update the state
          dispatch({
            operation: UPDATE_TLF,
            tlfData: response.data,
            newSavePoint: true,
            calledFrom: "TlfContextProvider::Save-Response",
          });
          sendSignalRPatch(response.data as T);
          // Update the holding object
          setTlfData?.(response.data as T);
          // Redirect to the new hierarchy if appropriate
          const targetHierarchy = hierarchyNodes?.find(
            (h) => h.id === response.data.primaryHierarchyId,
          );
          const newURL = targetHierarchy
            ? getTlfUrl(targetHierarchy, response.data)
            : // istanbul ignore next
              `/TlfDesigner/${response.data.azsolId}/${response.data.version}`;
          /* istanbul ignore else */
          if (newURL !== window.location.pathname) {
            navigateAfterSave(newURL);
          }
        }
      }
    },
    [
      appSettings.backEndUrl,
      hierarchy,
      hierarchyDispatch,
      hierarchyNodes,
      navigateAfterSave,
      saveRequestStatus,
      sendSignalRPatch,
      setTlfData,
      state.tlfData,
      tlfItemIndex,
    ],
  );

  // Patch request
  const [patchRequestStatus, setPatchRequestStatus] = useState<
    IRequestStatus<PatchTlfItemDetails<keyof TlfItemReadDto>>
  >({
    error: false,
    requesting: false,
  });

  // API call to patch a TLF Item
  const patchTlf = useCallback(
    async <K extends keyof TlfItemReadDto>(key: K, newValue: TlfItemReadDto[K]) => {
      /* istanbul ignore else */
      if (tlfData) {
        const patchDetails = {
          id: tlfData.id,
          azsolId: tlfData.azsolId,
          version: tlfData.version,
          key,
          newValue,
        };
        const response = await loadWrapper(
          patchTlfItem,
          patchDetails,
          patchRequestStatus,
          setPatchRequestStatus,
          appSettings.backEndUrl,
        );
        if (response?.success) {
          // Update the current state
          dispatch({
            operation: UPDATE_TLF,
            calledFrom: "TlfContextProvider:Patch-response",
            tlfData: response.data,
            newSavePoint: true,
          });
          // Update the holding object if possible, then let changes propagate
          if (setTlfData) setTlfData(response.data as T);
          sendSignalRPatch(response.data as T);
        }
      }
    },
    [appSettings.backEndUrl, patchRequestStatus, sendSignalRPatch, setTlfData, tlfData],
  );
  // Update tocPatching state
  useEffect(() => {
    if (patchRequestStatus.requesting && tocState.blocked !== "patching") {
      tocDispatch({ operation: SAVING_TOC_ITEM_VIA_TLF });
    }
  }, [patchRequestStatus, tocState.blocked, tocDispatch]);

  useEffect(() => {
    /**
     * Receives messages from SignalR and processes updates.
     * Note: Defining this function inside useEffect avoids certain issues.
     *
     * @param message - The message received from SignalR containing update information.
     */
    const updateFromSignalR = (message: string) => {
      const tlfUpdate = JSON.parse(message) as HubGroupMessage;
      const tlfUpdatePatch =
        tlfUpdate.text && tlfUpdate.text[0].startsWith("{")
          ? (JSON.parse(tlfUpdate.text[0]) as TlfUpdateActionSignal)
          : undefined;
      if (
        tlfUpdatePatch &&
        state.tlfData?.id === tlfUpdatePatch?.updatedItemId &&
        tlfUpdate.displayName !== signalR?.connection?.connectionId &&
        tlfUpdate.displayName !== "Backend" &&
        tlfUpdate.messageType === "tlfUpdate" &&
        !receivedMessages.map((r) => r.messageId).includes(tlfUpdate.messageId)
      ) {
        dispatch({
          operation: PROCESS_SIGNALR_NOTIFICATION,
          calledFrom: "SignalR",
          patch: tlfUpdatePatch.patch,
        });
        const newAction = processTlfActionFromSignal(tlfUpdate.messageId, tlfUpdatePatch);
        const newReceivedMessages = [
          ...receivedMessages.filter((m) => m.messageId !== newAction.receivedMessage.messageId),
          newAction.receivedMessage,
        ];
        setReceivedMessages(newReceivedMessages);
      } else if (
        tlfUpdate.groupName === `tlf${state.tlfData?.id}` &&
        tlfUpdate.displayName !== signalR?.connection?.connectionId &&
        tlfUpdate.displayName !== "Backend" &&
        tlfUpdate.text &&
        state.tlfData?.comments
      ) {
        switch (tlfUpdate.messageType) {
          case "deleteComment": {
            try {
              const tlfDelCommentId = JSON.parse(tlfUpdate.text[0]).id;
              /* istanbul ignore else */
              if (tlfDelCommentId) {
                dispatch({
                  operation: REMOVE_TLF_COMMENT,
                  commentId: tlfDelCommentId,
                  calledFrom: "SignalR:DeleteComment",
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing delete comment update:", error);
            }
            break;
          }
          case "comment": {
            try {
              const tlfUpdateMessage = JSON.parse(tlfUpdate.text[0]) as CommentReadDto;
              /* istanbul ignore else */
              if (tlfUpdateMessage) {
                dispatch({
                  operation: UPDATE_TLF_COMMENT,
                  comment: tlfUpdateMessage,
                  calledFrom: "SignalR:Comment",
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing comment update:", error);
            }
            break;
          }
          case "commentsPatch": {
            try {
              const commentsPatch = JSON.parse(tlfUpdate.text[0]) as Patch;
              /* istanbul ignore else */
              if (commentsPatch) {
                dispatch({
                  operation: PATCH_TLF_COMMENTS,
                  patch: commentsPatch,
                  calledFrom: "SignalR:CommentsPatch",
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing comments patch update:", error);
            }
            break;
          }
        }
      }
    };
    if (state.tlfData) {
      signalR?.connection?.on("SendMessageToGroup", updateFromSignalR);
    }
    // return () => {
    //   signalR?.connection?.off("SendMessageToGroup", updateFromSignalR);
    // };
  }, [receivedMessages, signalR?.connection, state.tlfData]);

  // Add effect to check for non-navigated window changes
  useEffect(() => {
    registerUnsavedChanges("TLF", state.changed);
  }, [registerUnsavedChanges, state.changed]);

  return (
    <TlfContext.Provider
      value={{
        ...state,
        dispatch,
        patchRequestStatus,
        patchTlf,
        readOnly:
          readOnly ||
          !hierarchyContext?.accessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy) ||
          (state.tlfData !== null &&
            ![eReleaseStatus.Draft, eReleaseStatus.ReleasePending].includes(
              state.tlfData.releaseStatus,
            )),
        receivedMessages,
        resetSaveRequest: () => setSaveRequestStatus({ requesting: false, error: false }),
        saveRequestStatus: saveRequestStatus,
        saveTlf,
      }}
    >
      {children}
    </TlfContext.Provider>
  );
};

TlfContextProvider.displayName = "TlfContextProvider";
